import { useState, useEffect } from 'react';
import { useLocation } from '@/contexts/LocationContext';

export interface NearbyPlace {
  name: string;
  place_id: string;
  address: string;
  lat: number;
  lng: number;
  country_code?: string;
}

interface UseNearbyRestaurantsResult {
  places: NearbyPlace[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

const MAX_RESULTS = 20;
const INCLUDED_TYPES = ['restaurant', 'lodging'];

export function useNearbyRestaurants(): UseNearbyRestaurantsResult {
  const { activeLocation, getRadiusInKm } = useLocation();
  const [places, setPlaces] = useState<NearbyPlace[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  const refetch = () => setFetchKey((k) => k + 1);

  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      setError('Google Maps API key not configured.');
      return;
    }

    setIsLoading(true);
    setError(null);

    const controller = new AbortController();

    fetch('https://places.googleapis.com/v1/places:searchNearby', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': apiKey,
        'X-Goog-FieldMask': 'places.id,places.displayName,places.formattedAddress,places.location,places.addressComponents',
      },
      body: JSON.stringify({
        locationRestriction: {
          circle: {
            center: {
              latitude: activeLocation.latitude,
              longitude: activeLocation.longitude,
            },
            radius: getRadiusInKm() * 1000,
          },
        },
        includedTypes: INCLUDED_TYPES,
        maxResultCount: MAX_RESULTS,
        languageCode: 'en',
      }),
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Nearby Search failed: ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const mapped: NearbyPlace[] = (data.places ?? []).map((p: any) => {
          const countryComponent = (p.addressComponents ?? []).find((c: any) =>
            c.types?.includes('country')
          );
          return {
            name: p.displayName?.text ?? 'Unknown',
            place_id: p.id,
            address: p.formattedAddress ?? '',
            lat: p.location?.latitude ?? 0,
            lng: p.location?.longitude ?? 0,
            country_code: countryComponent?.shortText,
          };
        });
        setPlaces(mapped);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        console.error('useNearbyRestaurants error:', err);
        setError('Could not load nearby restaurants.');
      })
      .finally(() => setIsLoading(false));

    return () => controller.abort();
  }, [activeLocation.latitude, activeLocation.longitude, getRadiusInKm, fetchKey]);

  return { places, isLoading, error, refetch };
}
